import { useRef, useState } from 'react';
import BrandSelect from './BrandSelect';
import LeadForm from './LeadForm';
import PageMeta from './PageMeta';
import { RedesignFooter, RedesignHeader, RedesignPage, SiteLink } from './RedesignShell';
import {
  choices,
  estimateProject,
  initialAnswers,
  label,
  projectBrief,
  range,
  services,
  summaryFor,
  validateBranch,
} from './calculatorModel';
import './project-tools.css';

function ChoiceGroup({ name, title, options = choices[name], value, onChange, multiple = false }) {
  return (
    <fieldset className="calc-fieldset">
      <legend>{title}</legend>
      <div className="calc-choices">
        {options.map(([key, text, description]) => (
          <label className="calc-choice" key={key}>
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name={name}
              value={key}
              checked={multiple ? value.includes(key) : value === key}
              onChange={() =>
                onChange(
                  multiple
                    ? value.includes(key)
                      ? value.filter((v) => v !== key)
                      : [...value, key]
                    : key,
                )
              }
            />
            <span>
              <strong>{text}</strong>
              {description && <small>{description}</small>}
            </span>
            <span className="calc-choice-mark" aria-hidden="true">
              {(multiple ? value.includes(key) : value === key) ? '✓' : '+'}
            </span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function CostSummary({ estimate, compact = false }) {
  const totals = [
    ['Разработка и разовые работы', estimate.project, 'за проект'],
    ['Регулярные услуги', estimate.month, 'в месяц'],
  ];
  return (
    <div className={'calc-costs' + (compact ? ' calc-costs-compact' : '')}>
      {totals
        .filter(([, total]) => total.max > 0)
        .map(([title, total, period]) => (
          <div className="calc-total" key={title}>
            <span>{title}</span>
            <strong>{range(total.min, total.max)}</strong>
            <small>{period}</small>
          </div>
        ))}
      {!estimate.project.max && !estimate.month.max && (
        <p>Выберите услугу и параметры — здесь появится ориентир.</p>
      )}
      {!compact && estimate.lines.some((line) => line.period === 'clarify' || line.quote) && (
        <ul className="calc-extra-costs">
          {estimate.lines
            .filter((line) => line.period === 'clarify' || line.quote)
            .map((line) => (
              <li key={line.title}>
                {line.title}:{' '}
                {line.quote ? 'по запросу' : range(line.min, line.max) + ', период уточняется'}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}

export default function CalculatorPage() {
  const preset =
    typeof window !== 'undefined' &&
    new URLSearchParams(window.location.search).get('project') === 'ecosystem';
  const [selected, setSelected] = useState(() => (preset ? ['site', 'app', 'mini'] : []));
  const [answers, setAnswers] = useState(() =>
    preset ? { ...initialAnswers, siteType: 'ecommerce' } : initialAnswers,
  );
  const [step, setStep] = useState('services');
  const [error, setError] = useState('');
  const [showContacts, setShowContacts] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const pdfLock = useRef(false);
  const headingRef = useRef(null);
  const steps = [
    'services',
    ...services.filter(([key]) => selected.includes(key)).map(([key]) => key),
    'timing',
    'result',
  ];
  const index = steps.indexOf(step);
  const estimate = estimateProject(selected, answers);
  const summary = summaryFor(selected, answers);
  const brief = projectBrief(selected, answers);
  const update = (key, value) => setAnswers((previous) => ({ ...previous, [key]: value }));
  const choose = (name, title, multiple = false, options) => (
    <ChoiceGroup
      name={name}
      title={title}
      value={answers[name]}
      onChange={(value) => update(name, value)}
      multiple={multiple}
      options={options}
    />
  );
  const titles = {
    services: 'Что вы хотите сделать?',
    site: 'Расскажите о сайте',
    app: 'Каким будет приложение?',
    mini: 'Где будут ваши клиенты?',
    seo: 'Какая задача у продвижения?',
    maps: 'Сколько адресов нужно продвигать?',
    timing: 'Бюджет и сроки',
    result: 'Ваш проект: состав и стоимость',
  };
  const move = (target) => {
    setStep(target);
    setError('');
    setShowContacts(false);
    // Фокус ведёт к вопросу; без принудительной плавной прокрутки и перехвата скролла.
    window.requestAnimationFrame(() => {
      headingRef.current?.focus({ preventScroll: true });
      headingRef.current?.scrollIntoView?.({ block: 'start', behavior: 'instant' });
    });
  };
  const next = () => {
    const message =
      step === 'services' && !selected.length
        ? 'Выберите хотя бы одно направление.'
        : validateBranch(step, answers);
    if (message) {
      setError(message);
      return;
    }
    if (step === 'timing') {
      const invalid = selected.find((key) => validateBranch(key, answers));
      if (invalid) {
        move(invalid);
        setError(validateBranch(invalid, answers));
        return;
      }
    }
    move(steps[index + 1]);
  };
  const selectField = (key, title) => (
    <div className="calc-text-field">
      <label id={'calc-' + key + '-label'} htmlFor={'calc-' + key}>
        {title}
      </label>
      <BrandSelect
        id={'calc-' + key}
        labelId={'calc-' + key + '-label'}
        value={answers[key]}
        options={choices[key]}
        onChange={(value) => update(key, value)}
      />
    </div>
  );
  const textField = (key, title, placeholder, type = 'text') => (
    <div className="calc-text-field">
      <label htmlFor={'calc-' + key}>{title}</label>
      <input
        id={'calc-' + key}
        type={type}
        value={answers[key]}
        onChange={(e) => update(key, e.target.value)}
        placeholder={placeholder}
        maxLength={255}
      />
    </div>
  );
  const download = async () => {
    if (pdfLock.current) return;
    pdfLock.current = true;
    setPdfBusy(true);
    setPdfError('');
    try {
      const { downloadProjectPdf } = await import('./projectPdf');
      await downloadProjectPdf(selected, answers);
    } catch (_error) {
      setPdfError('Не удалось подготовить PDF. Попробуйте скачать ещё раз.');
    } finally {
      pdfLock.current = false;
      setPdfBusy(false);
    }
  };
  return (
    <RedesignPage>
      <PageMeta
        title="Калькулятор услуг — расчёт стоимости проекта | Fatality Studio"
        description="Рассчитайте предварительную стоимость сайта, приложения, Mini App, SEO и продвижения в Яндекс.Картах. Состав проекта и оценка до отправки контактов."
      />
      <RedesignHeader />
      <main className="project-calculator">
        <section className="ink calc-intro">
          <div className="container">
            <div className="eyebrow cyan">— Калькулятор проекта</div>
            <h1>
              Рассчитайте стоимость
              <br />
              вашего проекта
            </h1>
            <p>
              Выберите услуги и ответьте на несколько вопросов. Состав проекта и предварительную
              стоимость покажем сразу — без телефона и регистрации.
            </p>
            <SiteLink href="/services/vseuslugi/" className="calc-back-link">
              Все услуги ↗
            </SiteLink>
          </div>
        </section>
        <section className="canvas calc-body">
          <div className="container calc-layout">
            <div className="calc-workspace">
              <div className="calc-progress">
                <span>
                  Шаг {index + 1} из {steps.length}
                </span>
                <span>{step === 'result' ? 'Расчёт готов' : 'Ответы можно изменить'}</span>
                <progress value={index + 1} max={steps.length} aria-label="Прогресс расчёта" />
              </div>
              <div className="calc-step" key={step}>
                <h2 ref={headingRef} tabIndex={-1}>
                  {titles[step]}
                </h2>
                {step === 'services' && (
                  <>
                    <p className="calc-help">
                      Можно выбрать несколько направлений. Вопросы будут только по ним.
                    </p>
                    <ChoiceGroup
                      name="services"
                      title="Направления проекта"
                      options={services}
                      value={selected}
                      multiple
                      onChange={(values) => {
                        setSelected(
                          services.filter(([key]) => values.includes(key)).map(([key]) => key),
                        );
                        if (values.includes('seo'))
                          setAnswers((a) => ({
                            ...a,
                            siteTasks: a.siteTasks.filter((task) => task !== 'seo'),
                          }));
                      }}
                    />
                  </>
                )}
                {step === 'site' && (
                  <>
                    {choose('siteExists', 'С чего начинаем?')}
                    {answers.siteExists === 'new' ? (
                      choose('siteType', 'Какой сайт нужен?')
                    ) : (
                      <>
                        {choose(
                          'siteTasks',
                          'Что нужно изменить?',
                          true,
                          choices.siteTasks.filter(
                            ([key]) => key !== 'seo' || !selected.includes('seo'),
                          ),
                        )}
                        {textField(
                          'siteUrl',
                          'Адрес сайта — необязательно',
                          'https://example.ru',
                          'url',
                        )}
                      </>
                    )}
                    {selected.includes('seo') && answers.siteExists === 'existing' && (
                      <p className="calc-help">
                        SEO уточним отдельно на следующем шаге и посчитаем один раз.
                      </p>
                    )}
                  </>
                )}
                {step === 'app' && (
                  <>
                    {choose('appPlatform', 'Платформа')}
                    {choose('appScope', 'Объём первой версии')}
                    {choose('appExtras', 'Дополнительно — необязательно', true)}
                    <p className="calc-help">
                      MVP считается как первая версия, а не как доплата к полной разработке.
                      Интеграции уточним по описанию проекта.
                    </p>
                  </>
                )}
                {step === 'mini' && (
                  <>
                    {choose('miniPlatforms', 'Выберите платформы', true)}
                    <p className="calc-help">
                      Можно выбрать несколько. Общую серверную часть и повторно используемые функции
                      учтём при подготовке сметы.
                    </p>
                  </>
                )}
                {step === 'seo' && (
                  <>
                    {choose('seoGoal', 'Основная задача')}
                    {textField(
                      'seoUrl',
                      'Адрес сайта — необязательно',
                      'https://example.ru',
                      'url',
                    )}
                    <p className="calc-help">
                      «SEO под ключ» уже включает комплекс работ. Отдельные направления к нему не
                      прибавляются.
                    </p>
                  </>
                )}
                {step === 'maps' && (
                  <>
                    {choose('mapsCount', 'Адреса организации')}
                    {textField(
                      'mapsName',
                      'Название организации — необязательно',
                      'Компания или ссылка на карточку',
                    )}
                  </>
                )}
                {step === 'timing' && (
                  <>
                    {selectField('budget', 'Бюджет — если уже определились')}
                    {selectField('deadline', 'Когда хотите запустить?')}
                    <p className="calc-help">
                      Ваш бюджет не меняет расчёт. Срок здесь — пожелание; возможность запуска
                      подтвердим после обсуждения.
                    </p>
                    <div className="calc-text-field">
                      <label htmlFor="calc-brief">Расскажите об идее — необязательно</label>
                      <textarea
                        id="calc-brief"
                        value={answers.brief}
                        onChange={(e) => update('brief', e.target.value)}
                        maxLength={1500}
                        rows={4}
                        placeholder="Задача, аудитория, нужные функции и интеграции"
                      />
                    </div>
                  </>
                )}
                {step === 'result' && (
                  <>
                    <p className="calc-help">
                      Предварительный диапазон, не фиксированная смета. Интеграции, общие работы,
                      лицензии и рекламный бюджет уточняются отдельно.
                    </p>
                    <CostSummary estimate={estimate} />
                    <div className="calc-breakdown">
                      <h3>Что вошло в оценку</h3>
                      {estimate.lines.map((line) => (
                        <div key={line.title}>
                          <span>{line.title}</span>
                          <strong>
                            {line.quote ? 'По запросу' : range(line.min, line.max)}
                            <small>
                              {line.period === 'month'
                                ? 'в месяц'
                                : line.period === 'clarify'
                                  ? 'период уточняется'
                                  : line.quote
                                    ? ''
                                    : 'за проект'}
                            </small>
                          </strong>
                        </div>
                      ))}
                    </div>
                    <p className="calc-help">
                      Диапазоны из предварительной модели калькулятора могут отличаться от стартовых
                      цен отдельных услуг. Итоговая стоимость рассчитывается после уточнения
                      деталей.
                    </p>
                    <div className="calc-result-actions">
                      <button
                        className="btn btn-primary"
                        onClick={() => setShowContacts(true)}
                        aria-expanded={showContacts}
                        aria-controls="calculator-contact"
                      >
                        Обсудить расчёт →
                      </button>
                      <button
                        className="btn calc-outline"
                        onClick={download}
                        disabled={pdfBusy}
                        aria-live="polite"
                      >
                        {pdfBusy ? 'Готовим PDF…' : 'Скачать расчёт'}
                      </button>
                    </div>
                    {pdfError && (
                      <p role="alert" className="calc-error">
                        {pdfError}
                      </p>
                    )}
                    {showContacts && (
                      <div id="calculator-contact" className="calc-contact">
                        <h3>Куда отправить подробную смету?</h3>
                        <p>
                          Передадим специалисту выбранные услуги, ответы и предварительный расчёт.
                        </p>
                        <LeadForm
                          key={brief}
                          formId="calculator-project-form"
                          idPrefix="calculator"
                          showTelegram={false}
                          showCompany={false}
                          contextDescription={brief}
                          projectType={
                            selected.length > 1 ? 'Комплексный проект' : summary[0]?.title
                          }
                          taskLabel="Комментарий — необязательно"
                          taskPlaceholder="Что ещё важно учесть"
                          submitLabel="Отправить проект на расчёт"
                          sentLabel="Заявка отправлена — свяжемся с вами ✓"
                        />
                      </div>
                    )}
                  </>
                )}
                {error && (
                  <p className="calc-error" role="alert">
                    {error}
                  </p>
                )}
                <div className="calc-nav">
                  {index > 0 && (
                    <button className="btn calc-outline" onClick={() => move(steps[index - 1])}>
                      ← Назад
                    </button>
                  )}
                  {step !== 'result' && (
                    <button className="btn btn-primary" onClick={next}>
                      {steps[index + 1] === 'result' ? 'Показать расчёт →' : 'Продолжить →'}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <aside className="calc-sidebar" aria-label="Состав вашего проекта">
              <div className="calc-sidebar-inner">
                <span className="calc-sheet-label">Ваш проект</span>
                <p className="calc-sheet-title">
                  От идеи
                  <br />к понятному плану.
                </p>
                {!selected.length && (
                  <p className="calc-help">Добавьте направления — здесь соберётся ваш проект.</p>
                )}
                <ul className="calc-project-list">
                  {summary.map((row) => (
                    <li key={row.key}>
                      <button onClick={() => move(row.key)} aria-label={'Изменить: ' + row.title}>
                        <span>
                          {row.title}
                          <small>{row.detail}</small>
                        </span>
                        <span aria-hidden="true">↗</span>
                      </button>
                    </li>
                  ))}
                </ul>
                {selected.length > 0 && (
                  <>
                    <CostSummary estimate={estimate} compact />
                    <p className="calc-help">
                      Предварительно. Для сложных и комплексных проектов итог уточняется после
                      брифинга.
                    </p>
                    {step === 'result' && (
                      <button className="calc-edit" onClick={() => move('timing')}>
                        Бюджет: {label('budget', answers.budget)}
                        <br />
                        Сроки: {label('deadline', answers.deadline)} ↗
                      </button>
                    )}
                  </>
                )}
                <span className="calc-sheet-footer">F/ · один проект, одна команда</span>
              </div>
            </aside>
          </div>
        </section>
      </main>
      <RedesignFooter />
    </RedesignPage>
  );
}
